import React, { FC, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  LottiePlayerECOMMBX,
  makeStyles,
  Theme,
  Typography,
} from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { getRoutePath } from '../../Routes/routePaths';

export type AppErrorProps = {
  fullHeight?: boolean;
  code?: string;
  message?: string;
  subtitle?: string;
  btnLabel?: string;
  onClickBtn?: () => void;
  retryBtnLabel?: string;
  onRetry?: () => void;
  className?: string;
  shouldShowButton?: boolean;
  isFullwidth?: boolean;
  isNumericCode?: boolean;
  isPaddedHorizontally?: boolean;
  shouldShowLottie?: boolean;
};

const useStyles = makeStyles<Theme>(({ spacing, breakpoints }) => ({
  section: ({ fullHeight }: { fullHeight?: boolean }) => ({
    height: fullHeight ? '100vh' : '100%',
    zIndex: 1,
    width: '100%',
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    flex: 1,
    [breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      textAlign: 'center',
    },
  }),
  ctaContainer: {
    [breakpoints.down('sm')]: {
      paddingTop: spacing(2),
    },
  },
  lottiePlayerContainer: {
    height: '416px',
    width: '50%',
    [breakpoints.down('md')]: {
      marginBottom: spacing(6),
      height: '270px',
      width: '50%',
    },
    [breakpoints.down('sm')]: {
      height: '256px',
      width: '256px',
      margin: 0,
    },
  },
  fullWidth: {
    width: '100%',
  },
}));

const AppError: FC<AppErrorProps> = ({
  fullHeight,
  code,
  message,
  subtitle,
  btnLabel,
  onClickBtn,
  onRetry,
  retryBtnLabel,
  shouldShowButton = true,
  isPaddedHorizontally = false,
  className,
  isFullwidth,
  isNumericCode = true,
  shouldShowLottie = true,
}) => {
  const classes = useStyles({ fullHeight });
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const { t } = useTranslation();

  const onClick = useCallback(() => {
    if (onClickBtn) {
      onClickBtn();
    } else {
      window.location.href = getRoutePath({ routeKey: '' });
    }
  }, [onClickBtn]);

  return (
    <Grid container className={`${classes.section} ${className}`}>
      <Box
        pt={isMobile ? 0 : 4}
        px={isMobile ? 4 : isPaddedHorizontally ? 4 : 0}
        width={isMobile || isFullwidth ? '100%' : 'auto'}>
        <Typography color='primary' component={'h1'} variant={isMobile ? 'h4' : 'h2'} noWrap={!code?.includes(' ')}>
          {`${isNumericCode ? t('appError.header') + ' ' + (code ?? '404') : code}`}
        </Typography>
        {message && (
          <Typography color='secondary' variant={isMobile ? 'h6' : 'h5'} noWrap={!message?.includes(' ')}>
            {message}
          </Typography>
        )}
        {subtitle && (
          <Box mt={4}>
            <Typography color='textSecondary' noWrap={!subtitle?.includes(' ')}>
              {subtitle.length > 120 ? subtitle.substring(0, 50).concat('...') : subtitle}
            </Typography>
          </Box>
        )}
        {isMobile && shouldShowLottie && (
          <Grid container justifyContent='center'>
            <Grid item>
              <Box className={classes.lottiePlayerContainer}>
                <LottiePlayerECOMMBX icon='ecommbx_astronaut' loop play />
              </Box>
            </Grid>
          </Grid>
        )}
        <Grid container direction={isMobile ? 'column' : 'row'} className={classes.ctaContainer}>
          {onRetry && typeof onRetry === 'function' && (
            <Grid item>
              <Box mt={isMobile ? 2 : 4} mr={isMobile ? 0 : 1}>
                <Button color='primary' size='small' variant='outlined' onClick={onRetry} fullWidth={isMobile}>
                  {retryBtnLabel}
                </Button>
              </Box>
            </Grid>
          )}
          {shouldShowButton && (
            <Grid item>
              <Box mt={isMobile ? 2 : 4} mb={!shouldShowLottie && !isMobile ? 4 : 0} ml={isMobile ? 0 : onRetry && typeof onRetry === 'function' ? 1 : 0}>
                <Button color='primary' size='small' variant='contained' onClick={onClick} fullWidth={isMobile}>
                  {btnLabel}
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
      {!isMobile && shouldShowLottie && (
        <Box className={classes.lottiePlayerContainer}>
          <LottiePlayerECOMMBX icon='ecommbx_astronaut' loop play />
        </Box>
      )}
    </Grid>
  );
};

export { AppError };
